import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useI18n } from '@whizz/react-i18n';
import type { FilterValueType, ReportFilterModalProps } from '../../global/modals/ReportFilterModal';
import type { ClassReportStudentType, ReportsFilterOptionsType } from '../../pages/types';
import type {
  ReportFilterGenderValue,
  ReportFilterProgressionValue,
  ReportFilterQualityValue,
  ReportFilterStudentsValue,
  ReportFilterTagValue,
} from '../../global/modals/ReportFilterModal.views';
import { Button, ButtonIcon, Tooltip } from '../../components';
import { ButtonGroup } from '../../components/button/Button.styled';
import { useModal } from '../useModal';
import { ReportFilterModal } from '../../global';
import { GLOBAL_ICONS } from '../../util/vars';
import { useLocalStorage, usePrevious } from 'react-use';
import type { Tag } from '@whizz/api-model/dist/@types';
import { isEqualWith } from 'lodash';

const DEFAULT_SHOW_OTHERS_DATA = false;

export const useFilterSelector = (students: ClassReportStudentType[], tags: Tag[] | null): [
  React.ReactElement,
  FilterValueType,
  boolean,
  string,
  (clearShowOthersData: boolean) => void,
  (numberOfFilteredStudents: number | null) => void,
  (filter: FilterValueType, show: boolean) => void,
] => {
  const { translate } = useI18n();
  const [ showOthersData, setShowOthersData ] = useState(false);
  const [
    lastSessionShowOthersData,
    setLastSessionShowOthersData,
  ] = useLocalStorage('last_session_show_other_class_data', DEFAULT_SHOW_OTHERS_DATA);
  const [ value, setValue ] = useState<FilterValueType>(null);
  const [ numberOfFilteredStudents, setNumberOfFilteredStudents ] = useState<number | null>(null);
  const previousStudents = usePrevious(students);

  const handleSubmit = (filter: FilterValueType, show: boolean) => {
    const localShowOthersData = null == filter ? true : show;

    setShowOthersData(localShowOthersData);
    setLastSessionShowOthersData(localShowOthersData);

    if (null == filter || ('student' in filter && 0 === filter.student.length)) {
      setValue(null);
      return;
    }

    setValue(filter);
  };

  const clearFilters = (clearShowOthersData = true) => {
    setProps((prev) => ({
      ...prev,
      filter: null,
      show: clearShowOthersData ? DEFAULT_SHOW_OTHERS_DATA : prev.show,
    }));

    setValue(null);

    if (!clearShowOthersData) {
      return;
    }

    setShowOthersData(DEFAULT_SHOW_OTHERS_DATA);
    setLastSessionShowOthersData(DEFAULT_SHOW_OTHERS_DATA);
  };

  const [ modal, toggle, setProps ] = useModal((props: ReportFilterModalProps) => (
    <ReportFilterModal {...props} />
  ), {
    size: 'lg',
    disableHeader: true,
    students: [],
    filter: null,
    show: showOthersData,
    clearFilters: clearFilters,
    onSubmit: handleSubmit,
    tags: null,
  });

  const getValueLabel = (filter: FilterValueType): string => {
    if (filter) {
      const filterSelected = Object.keys(filter)[0] as ReportsFilterOptionsType;

      switch (filterSelected) {
        case 'student':
          if ((filter as ReportFilterStudentsValue).student.length > 1) {
            return `${(filter as ReportFilterStudentsValue).student.length} ${translate('global.STUDENT_plural')}`;
          } else {
            return students.length ? students.find((student) => student.id === (filter as ReportFilterStudentsValue).student[0])!.fullName : '';
          }
        case 'quality':
          return translate(`pages.REPORTS.FILTERS.MODAL.OPTIONS.QUALITY.${(filter as ReportFilterQualityValue).quality}`);
        case 'progression':
          return translate(`pages.REPORTS.FILTERS.MODAL.OPTIONS.PROGRESSION.${(filter as ReportFilterProgressionValue).progression}`);
        case 'gender':
          return translate(`pages.REPORTS.FILTERS.MODAL.OPTIONS.GENDER.${(filter as ReportFilterGenderValue).gender}`);
        case 'tag':
          // eslint-disable-next-line no-case-declarations
          const tag = tags?.find((tag) => tag.id === (filter as ReportFilterTagValue).tag);
          return tag?.abbreviation ? translate(`pages.ROSTER_MANAGEMENT.TAGS.DEFAULT_TAGS_ABBREVIATIONS.${tag.abbreviation}`, { 'defaultValue': tag.abbreviation }) : '';
        default:
          return '';
      }
    }

    return '';
  };

  useEffect(() => {
    if (null == lastSessionShowOthersData) {
      return;
    }

    setShowOthersData(lastSessionShowOthersData);
  }, [ lastSessionShowOthersData ]);

  useEffect(() => {
    setProps((prev) => ({
      ...prev,
      students: students,
      tags: tags,
      onSubmit: handleSubmit,
    }));

    if (null == value || !('student' in value) || null == value.student) {
      return;
    }
    if ('student' in value && isEqualWith(students, previousStudents, (a, b) => a.id === b.id)) {
      return;
    }
    setValue(null);
  }, [ students, tags ]);

  const filterLabel = value ? getValueLabel(value) : '';
  const buttonComponent = (
    <>
      {modal}
      {value ? (
        <ButtonGroup>
          <CustomFilterButton
            color={'primary'}
            isOutline
            highlight
            onClick={() => {
              setProps((prev) => ({
                ...prev,
                filter: value,
                show: showOthersData,
              }));
              toggle(true);
            }}
            data-cy="filter-button"
          >
            <div className='flex flex-col sm:flex-row'>
              {filterLabel}
              {numberOfFilteredStudents !== null ? <span className={'font-bold italic'}><span className={'hidden sm:inline whitespace-pre'}> - </span>{translate('pages.REPORTS.FILTERS.STUDENTS', { count: numberOfFilteredStudents })}</span> : null}
            </div>
          </CustomFilterButton>
          <Tooltip
            tooltipMessage={translate('pages.REPORTS.FILTERS.CLEAR')}
          >
            <CustomFilterButtonIcon
              size={'sm'}
              color={'primary'}
              isOutline
              highlight
              icon={GLOBAL_ICONS.close}
              onClick={() => clearFilters()}
              data-cy="filter-clear"
            />
          </Tooltip>
        </ButtonGroup>
      ) : (
        <CustomFilterButton
          disabled={students.length === 0}
          color={'primary'}
          isOutline
          onClick={() => {
            setProps((prev) => ({
              ...prev,
              filter: null,
              show: lastSessionShowOthersData ?? false,
            }));
            toggle(true);
          }}
          data-cy="filter-button"
        >
          {translate('pages.REPORTS.FILTERS.PLACEHOLDER')}
        </CustomFilterButton>
      )}
    </>
  );

  return [
    buttonComponent,
    value || null,
    // If no filters selected, show others data always
    null != value ? showOthersData : true,
    filterLabel,
    clearFilters,
    setNumberOfFilteredStudents,
    handleSubmit,
  ];
};

const CustomFilterButton = styled(Button)`
  [data-icon-filter="true"] {
    path {
      &:first-child {
        fill: ${(props) => props.theme.colors.header} !important;
      }
      &:last-child {
        fill: #1C335A !important;
      }
    }
  }
  border-radius: ${(props) => props.theme.radius.input};
  ${(props) => props.highlight && 'border-width: 3px;'}
`;

const CustomFilterButtonIcon = styled(ButtonIcon)`
  ${(props) => props.highlight && 'border-width: 3px;'}
`;
